/*
  Displays all billing plans (ie. rate schedules) available to an auto group
  based on the supplied groupId parameter.
  
  Encapsulates all logic to render the additional charges modal as well.
  Can handle displaying multiple rate schedule cards.

  This is used to show to the user which plans they can choose from in the AutoGroup editor
  found under Manage Groups.
*/

import RateScheduleCollectionSelector from './rate_schedule_collection_selector';

import {RateScheduleCard, EnterpriseRateScheduleCard} from 'admin/accounting/rate_schedule_cards'
import RateScheduleCollectionCompare from 'admin/accounting/rate_schedule_collection_compare'
import {Permissions} from '../../utils/permissions'

export class AutoGroupRateScheduleCards extends HTMLElement {
  constructor() {
    super();
  }

  async loadData(){
    let url = `/admin/groups/${this.groupId}/rate_schedules`
    let res = await fetch(url);
    this.data = await res.json();
    // console.log('AutoGroupRateScheduleCards data: ',this.data)
  }
  
  async connectedCallback() {
    this.groupId = this.getAttribute("groupId")
    this.selectedRateScheduleId = this.getAttribute("selectedRateScheduleId")
    this.selectedRateScheduleGroupId = this.getAttribute("selectedRateScheduleGroupId")
    this.selectedRateScheduleGroupName = this.getAttribute("selectedRateScheduleGroupName")
    this.showCurrentPlan = this.getAttribute('showCurrentPlan') || false;
    // convert "true" to true
    this.showCurrentPlan = (this.showCurrentPlan === "true")
    

    await this.loadData()

    this.innerHTML = `
      <div class="row">
        <template id="additional_charges_template">      
          <div class="content">
            <table class="pricing-table">
              <colgroup>
                <col class="col-headers" />
                <col class="col-data" />
              </colgroup>
              <thead>
                <tr>
                  <td class="info">
                    <p class="title"></p>
                  </td>
                  <th scope="col">
                    <p class="tier">
                      Starting at <b><span class="price"></span></b>
                    </p>
                    <p>
                      monthly fee plus<br />additional usage.
                    </p>
                  </th>
                </tr>
              </thead>
            </table>
          </div>
        </template>

        <div id="rate_schedule_collections_menu_container"></div>
        <div class="billing_plans_container"></div>
        <div id="billing_plans_comparison_container" style="margin: 20px 0px"></div>
      </div>

      <additional-charges-modal></additional-charges-modal>
      `;


    // show a menu of rate schedule collections if the group
    // is not associated with a collection.
    if (this.data.length === 0) {
      this.renderCollectionMenu()
    }
    else {
      if(await Permissions.hasPermissionForGroup(Permissions.ALL.admin_is_admin, this.groupId)) {
        // allow for collection to be edited
        this.renderCollectionEditButton()
      }
    }

    this.renderRateScheduleCards()    
  }  // connectedCallback

  renderCollectionEditButton() {
    const menu_container = this.querySelector('#rate_schedule_collections_menu_container')
    if (! menu_container) return
    menu_container.innerHTML = `
      <div class="form-group form-horizontal col-xs-12" style="padding-bottom: 20px" >
        <div class="col-md-offset-8 col-sm-4 no-gutters">
          <div class="row" style="margin: 8px 0px; text-align: right">
            <label>Rate Schedule Collection:</label>
            <span id="select_rate_schedule_group_name">${this.selectedRateScheduleGroupName}</span>
          </div>
          <a class="btn btn-sm btn-secondary pull-right" id="edit_collection">Change Collection</a>
        </div>
      </div>
    `
    this.querySelector('#edit_collection').addEventListener('click', this.renderCollectionMenu.bind(this) )
  }
  
  renderCollectionMenu() {
    const menu_container = this.querySelector('#rate_schedule_collections_menu_container')
    if (menu_container){
      menu_container.innerHTML = ''
      menu_container.appendChild(new RateScheduleCollectionsMenu(this.groupId, this.selectedRateScheduleGroupId))  
    }

    const comparison_container = this.querySelector('#billing_plans_comparison_container')
    if (comparison_container){
      comparison_container.innerHTML = ''
      comparison_container.appendChild(new RateScheduleCollectionCompare(this.groupId))    
    }
  }

  // Only used by the rate-schedule-collections-menu component when selecting
  // a collection. Load the rate schedules in the collection to be rendered.
  async loadCollection(rate_schedule_collection_id) {
    if (rate_schedule_collection_id === null) {
      this.data = []
    }
    else {
      if (rate_schedule_collection_id === parseInt(this.selectedRateScheduleGroupId)) {
        this.differentCollectionSelected = false
        await this.loadData()
        this.showCurrentPlan = true

        // show a menu of rate schedule collections if the group
        // is not associated with a collection.
        if (this.data.length === 0) {
          this.renderCollectionMenu()
        }
        else {
          // allow for collection to be edited
          this.renderCollectionEditButton()
        }        
      }
      else {
        let url = `/admin/rate_schedule_groups/${rate_schedule_collection_id}/rate_schedules`
        let res = await fetch(url);
        const rate_schedules = await res.json();
        this.data = rate_schedules
        this.differentCollectionSelected = true
      }
    }

    this.renderRateScheduleCards()
  }
  
  renderRateScheduleCards() {
    const billing_plans_container = this.querySelector('.billing_plans_container');
    billing_plans_container.innerHTML = ''

    if (this.data.length > 0) {
      this.data.map(rate_schedule => {
        rate_schedule['show_current_plan'] = this.showCurrentPlan
        const upgradable = true
        const autoGroupHasSelectedRateSchedule = this.selectedRateScheduleId > 0
        billing_plans_container.appendChild(new RateScheduleCard(rate_schedule, upgradable, autoGroupHasSelectedRateSchedule, this.differentCollectionSelected))        
      })

      
      if (this.data.length > 0) {
        const call_us_number = this.data[0].billing_settings.call_us_number

        // generate the enterprise rate schedule dummy card as it will always be the last one.
        billing_plans_container.appendChild(new EnterpriseRateScheduleCard(call_us_number))      
      }

      // listener to change billing plans and re-render ui elements.
      // triggered when user selects a rate schedule card.
      if (this.querySelector(".change-billing-plan")) {
        this.querySelectorAll(".change-billing-plan").forEach( element => {
          element.addEventListener("click", async function (e) {
            e.preventDefault();
            await this.handleChangeBillingPlan(e.currentTarget)
          }.bind(this));  
        }) 
      } 
    }
  }
  
  // send request to change the billing plan.
  // broadcast event to re-render other ui elements if successful.
  async handleChangeBillingPlan(obj) {

    let conf = await confirmPlus(`Are you sure you want to change your subscription to "${obj.getAttribute("data-plan-name")}"?`, 'Change Billing Plans');
    if (!conf){ return }
    
    const rate_schedule_id = obj.getAttribute("data-plan-id")
    const auto_group_id = this.getAttribute("groupId")
    const url = `/admin/groups/${auto_group_id}/change_billing_plan`
    let post = {
      rate_schedule_id: rate_schedule_id
      }
    
    pageLoadingFrame("show")

    let res = await fetch(url, {
      headers: {'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
                  'Content-type': 'application/json'},
      method: 'POST',
      body: JSON.stringify(post)
    }).catch(error => {
      pageLoadingFrame("hide")
      console.error('There was an error!', error);
      Common.notify(result.error, 'danger')
    }); 

    const result = await res.json();

    pageLoadingFrame("hide")

    if (result.error) {
      Common.notify(result.error, 'danger')
    }
    else {
      Common.notify(result.msg, 'success')
      this.dispatchEvent(new CustomEvent('billing_plan_changed', 
        {bubbles: true, composed: true, detail: {rate_schedule_id: result.rate_schedule_id ,rate_schedule_group: result.rate_schedule_group}}
      ))
    }
  }
}
customElements.define("auto-group-rate-schedule-cards", AutoGroupRateScheduleCards)


/*
  Displays a list of active rate schedule collections that have at least 1 rate schedule in them.
  When a collection is selected, send message to the auto-group-rate-schedule-cards component to
  load the rate schedules within the selected collection.
*/
class RateScheduleCollectionsMenu extends HTMLElement {
  constructor(groupId, selectedRateScheduleGroupId=null) {
    super();
    this.groupId = groupId
    this.selectedRateScheduleGroupId = parseInt(selectedRateScheduleGroupId)
    this.selectedIndustryIds = []
  }

  async loadData(){
    let url = `/admin/groups/rate_schedule_collections?q=${this.selectedIndustryIds}`
    let res = await fetch(url);
    this.data = await res.json();
    // console.log('RateScheduleCollectionsMenu data: ',this.data)
  }
  

  async connectedCallback() {

    this.innerHTML = "Loading...";

    await this.loadData()

    this.innerHTML = `
    <div class="frame form-group form-horizontal col-xs-12" style="margin-bottom: 20px">
      <div class="col" style="margin-bottom: 4px">
        <div class="pull-left">
          <h4>Rate Schedule Collection</h4>
        </div>
        <div class="pull-right">
          <a class="btn btn-primary" id="update_collection" 
          ${this.selectedRateScheduleGroupId > 0 ? "" : "disabled"}
          >Change Collection</a>
        </div>
        <br/>
      </div>

        <div id="industry_selector" style="padding: 20px 0px 12px 0px""></div>

        <label>Rate Schedule Collections</label>
        <rate-schedule-collection-selector 
          data_selected_rate_schedule_group_id=${this.selectedRateScheduleGroupId}
          >
        </rate-schedule-collection-selector>


    </div>    
    `

    this.querySelector('#update_collection').addEventListener('click', this.handleUpdateCollection.bind(this) )

    // broadcast message from DropdownTreeSelect
    this.addEventListener('collection_selection_changed', function(e){
      const collection_id = (e.detail.data.length > 0) ? e.detail.data[0].value : null
      this.new_collection_id = collection_id

      if (this.new_collection_id !== this.selectedRateScheduleGroupId) {
        this.querySelector('#update_collection').removeAttribute("disabled")
      }
      else {
        this.querySelector('#update_collection').setAttribute("disabled", "disabled")
      }
      
      this.handleCollectionChange(collection_id)
    })
    
    this.filter_by_industries([]) 

    let row = this.querySelector("#industry_selector")
    if (row) {
      let el = document.createElement("rate-schedule-group-industries");
      el.id = this.groupId;
      el.current_selected_industries = [];
      el.allow_all_industries=true
      el.label_text = "Filter by Industries"
      row.appendChild(el);
      this.addEventListener("selected_industries_changed", function(e){
        this.filter_by_industries(e.detail.selected_industries)
      }.bind(this))
    }
  }

  async filter_by_industries(selected_industry_ids) {
    this.selectedIndustryIds = selected_industry_ids
    await this.loadData()

    const menu = this.querySelector('rate-schedule-collection-selector')
    if (menu) {
      menu.update_collections(this.data)

      if (this.data.length === 0) {
        this.handleCollectionChange(null)
      }
    }
  }
  
  handleCollectionChange(rate_schedule_collection_id) {
    const update_button = this.querySelector("#update_collection")
    const rate_schedule_cards_component = document.querySelector('auto-group-rate-schedule-cards')
    const compare_component = document.querySelector('rate-schedule-collection-compare')

    // console.log('[handleCollectionChange] selected collection id: ',rate_schedule_collection_id)
    if (! isNaN(rate_schedule_collection_id)) {
      // pass the rate schedule collection id to component to show the rate schedule cards in the collection.
      if (rate_schedule_cards_component) {
        rate_schedule_cards_component.loadCollection(rate_schedule_collection_id)
      }

      if (compare_component) {
        compare_component.loadCollection(rate_schedule_collection_id)
      }

      if (update_button) {
        update_button.removeAttribute("disabled")
      }
    }
    else {
      if (update_button) {
        update_button.setAttribute("disabled","disabled")
      }

      if (rate_schedule_cards_component) {
        rate_schedule_cards_component.loadCollection(null)
      }

      if (compare_component) {
        compare_component.loadCollection(null)
      }
    }
  }


  async handleUpdateCollection(e) {
    if (! this.new_collection_id) return 

    const collection_id = this.new_collection_id
    const url = `/admin/groups/${this.groupId}/update_collection`
    let post = {
      rate_schedule_group_id: collection_id
      }
    
    pageLoadingFrame("show")

    let res = await fetch(url, {
      headers: {'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
                  'Content-type': 'application/json'},
      method: 'POST',
      body: JSON.stringify(post)
    }).catch(error => {
      pageLoadingFrame("hide")
      console.error('There was an error!', error);
      Common.notify(result.error, 'danger')
    }); 

    const result = await res.json();

    pageLoadingFrame("hide")

    if (result.error) {
      Common.notify(result.error, 'danger')
    }
    else {
      Common.notify(result.msg, 'success')
      this.dispatchEvent(new CustomEvent('billing_plan_changed', 
        {bubbles: true, composed: true, detail: {new_rate_schedule_group: result.rate_schedule_group}}
      ))
    }
  }
}
customElements.define("rate-schedule-collections-menu", RateScheduleCollectionsMenu);

